import { format, parseISO, isValid } from 'date-fns';
import config from '../config';

class DataProcessor {
  // Process batch data for display
  processBatchData(batches) {
    if (!Array.isArray(batches)) return [];
    
    return batches.map(batch => ({
      ...batch,
      date_formatted: this.formatDate(batch.date),
      processing_date: this.formatDate(batch.processing_timestamp),
      total_sequences: parseInt(batch.total_sequences) || 0,
      status: this.determineBatchStatus(batch)
    })).sort((a, b) => new Date(b.processing_timestamp) - new Date(a.processing_timestamp));
  }

  // Process executive summary data
  processExecutiveSummary(summary) {
    if (!summary?.executive_summary) return null;

    const exec = summary.executive_summary;
    return {
      ...summary,
      alert_level_info: this.getAlertLevelInfo(exec.alert_level),
      metrics: {
        total_sequences: parseInt(exec.total_sequences) || 0,
        countries_represented: parseInt(exec.countries_represented) || 0,
        critical_mutations: parseInt(exec.critical_mutations_detected) || 0,
        novel_mutations: parseInt(exec.novel_mutations_discovered) || 0
      },
      risk_distribution_processed: this.processRiskDistribution(exec.risk_distribution),
      recommendations_prioritized: this.prioritizeRecommendations(exec.recommendations)
    };
  }

  // Process mutations data for tables and charts
  processMutationsData(mutations) {
    if (!Array.isArray(mutations)) return [];

    return mutations.map(mutation => ({
      ...mutation,
      frequency: parseInt(mutation.frequency) || 0,
      prevalence_percentage: mutation.prevalence ? parseFloat(mutation.prevalence) : 0,
      risk_score: parseFloat(mutation.risk_score) || 0,
      risk_level_info: this.getRiskLevelInfo(mutation.risk_level),
      countries_affected: mutation.countries?.length || 0,
      first_detection_formatted: this.formatDate(mutation.first_detection),
      category_display: this.formatMutationCategory(mutation.category)
    })).sort((a, b) => b.risk_score - a.risk_score);
  }

  // Process geographic data for maps and charts
  processGeographicData(geoData) {
    if (!Array.isArray(geoData)) return [];

    return geoData.map(country => ({
      ...country,
      sequence_count: parseInt(country.sequence_count || country.count) || 0,
      novel_mutations_count: parseInt(country.novel_mutations_count) || 0,
      risk_distribution_processed: this.processRiskDistribution(country.risk_distribution),
      // Use dominant_risk from backend if available, otherwise calculate it
      dominant_risk: country.dominant_risk || this.getDominantRiskLevel(country.risk_distribution),
      date_formatted: this.formatDate(country.date)
    })).sort((a, b) => b.sequence_count - a.sequence_count);
  }

  // Process chart data for visualizations
  processChartData(chartData) {
    if (!chartData) return {};

    return {
      risk_distribution: this.formatRiskDistributionChart(chartData.risk_distribution),
      geographic_distribution: this.formatGeographicChart(chartData.geographic_distribution),
      host_distribution: this.formatHostChart(chartData.host_distribution),
      mutation_frequency: this.formatMutationChart(chartData.mutation_frequency),
      temporal_data: this.formatTemporalChart(chartData.temporal_data)
    };
  }

  // Process real-time alerts
  processAlerts(alerts) {
    if (!Array.isArray(alerts)) return [];

    return alerts.map(alert => ({
      ...alert,
      urgency_score: parseFloat(alert.urgency_score) || 0,
      novelty_score: parseFloat(alert.novelty_score) || 0,
      detection_date_formatted: this.formatDateTime(alert.detection_date),
      time_ago: this.getTimeAgo(alert.detection_date),
      alert_level_info: this.getAlertLevelInfo(alert.alert_level),
      priority: this.calculateAlertPriority(alert)
    })).sort((a, b) => b.urgency_score - a.urgency_score);
  }

  // Process sequence search results
  processSequenceData(sequences) {
    if (!Array.isArray(sequences)) return [];

    return sequences.map(seq => ({
      ...seq,
      risk_score: parseFloat(seq.total_risk_score) || 0,
      mutations_count: parseInt(seq.mutations_count) || 0,
      novel_mutations_count: parseInt(seq.novel_mutations_count) || 0,
      processing_date: this.formatDate(seq.processing_timestamp),
      collection_date: this.formatDate(seq.collection_date),
      risk_level_info: this.getRiskLevelInfo(seq.risk_level)
    }));
  }

  // Process temporal analysis data
  processTemporalData(temporalData) {
    if (!temporalData) return null;

    return {
      risk_timeline: this.formatTimelineData(temporalData.risk_timeline),
      daily_statistics: this.formatDailyStats(temporalData.daily_statistics),
      trends: this.calculateTrends(temporalData.risk_timeline)
    };
  }

  // Helper methods
  formatDate(dateString) {
    if (!dateString) return 'Unknown';
    
    try {
      const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
      return isValid(date) ? format(date, config.DATE_FORMATS.DISPLAY) : 'Invalid Date';
    } catch (error) {
      return 'Invalid Date';
    }
  }

  formatDateTime(dateString) {
    if (!dateString) return 'Unknown';
    
    try {
      const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
      return isValid(date) ? format(date, config.DATE_FORMATS.DATETIME) : 'Invalid Date';
    } catch (error) {
      return 'Invalid Date';
    }
  }

  getTimeAgo(dateString) {
    if (!dateString) return 'Unknown';
    
    try {
      const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${diffDays}d ago`;
    } catch (error) {
      return 'Unknown';
    }
  }

  getRiskLevelInfo(riskLevel) {
    return config.RISK_LEVELS[riskLevel?.toUpperCase()] || config.RISK_LEVELS.UNKNOWN;
  }

  getAlertLevelInfo(alertLevel) {
    return config.ALERT_LEVELS[alertLevel?.toUpperCase()] || config.ALERT_LEVELS.GREEN;
  }

  processRiskDistribution(riskDist) {
    if (!riskDist) return {};

    const total = Object.values(riskDist).reduce((sum, val) => sum + (parseInt(val) || 0), 0);
    
    return Object.entries(riskDist).map(([level, count]) => {
      const numCount = parseInt(count) || 0;
      return {
        level,
        count: numCount,
        percentage: total > 0 ? Math.round((numCount / total) * 100) : 0,
        color: this.getRiskLevelInfo(level).color,
        label: this.getRiskLevelInfo(level).label
      };
    }).sort((a, b) => this.getRiskLevelInfo(b.level).priority - this.getRiskLevelInfo(a.level).priority);
  }

  getDominantRiskLevel(riskDist) {
    if (!riskDist) return 'UNKNOWN';

    return Object.entries(riskDist)
      .reduce((max, [level, count]) => 
        (parseInt(count) || 0) > (parseInt(max.count) || 0) ? { level, count } : max, 
        { level: 'UNKNOWN', count: 0 }
      ).level;
  }

  formatMutationCategory(category) {
    if (!category) return 'Other';
    return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  prioritizeRecommendations(recommendations) {
    if (!Array.isArray(recommendations)) return [];

    return recommendations.map((rec, index) => ({
      text: rec,
      priority: rec.toLowerCase().includes('urgent') || rec.toLowerCase().includes('immediate') ? 'HIGH' : 'MEDIUM',
      order: index
    })).sort((a, b) => {
      if (a.priority === 'HIGH' && b.priority !== 'HIGH') return -1;
      if (b.priority === 'HIGH' && a.priority !== 'HIGH') return 1;
      return a.order - b.order;
    });
  }

  determineBatchStatus(batch) {
    const sequences = parseInt(batch.total_sequences) || 0;
    const timestamp = batch.processing_timestamp;
    
    if (sequences === 0) return 'EMPTY';
    if (!timestamp) return 'PENDING';
    
    const now = new Date();
    const processedDate = new Date(timestamp);
    const ageHours = (now - processedDate) / (1000 * 60 * 60);
    
    if (ageHours < 1) return 'RECENT';
    if (ageHours < 24) return 'CURRENT';
    return 'ARCHIVED';
  }

  calculateAlertPriority(alert) {
    let score = 0;
    
    if (alert.alert_level === 'CRITICAL') score += 40;
    else if (alert.alert_level === 'HIGH') score += 30;
    else if (alert.alert_level === 'MEDIUM') score += 20;
    else score += 10;
    
    score += (parseFloat(alert.novelty_score) || 0) * 10;
    score += (parseFloat(alert.urgency_score) || 0);
    
    if (alert.country === 'united states') score += 5;
    if (alert.host === 'human') score += 15;
    
    return Math.min(100, score);
  }

  // Chart formatting methods
  formatRiskDistributionChart(data) {
    if (!Array.isArray(data)) return [];
    
    return data.map(item => ({
      name: this.getRiskLevelInfo(item._id).label,
      value: item.count || 0,
      fill: this.getRiskLevelInfo(item._id).color
    }));
  }

  formatGeographicChart(data) {
    if (!Array.isArray(data)) return [];
    
    return data.map(item => ({
      country: item._id,
      sequences: item.count || 0,
      risk_levels: item.risk_levels || []
    })).slice(0, 20); // Limit to top 20 countries
  }

  formatHostChart(data) {
    if (!Array.isArray(data)) return [];
    
    return data.map((item, index) => ({
      host: item._id,
      count: item.count || 0,
      avg_risk: parseFloat(item.avg_risk_score) || 0,
      fill: config.CHART_COLORS[index % config.CHART_COLORS.length]
    })).slice(0, 15); // Limit to top 15 hosts
  }

  formatMutationChart(data) {
    if (!Array.isArray(data)) return [];
    
    return data.map((item, index) => ({
      mutation: item._id,
      frequency: item.count || 0,
      risk_level: item.risk_level,
      fill: config.CHART_COLORS[index % config.CHART_COLORS.length]
    }));
  }

  formatTimelineData(timeline) {
    if (!Array.isArray(timeline)) return [];
    
    return timeline.map(item => ({
      date: item.date,
      date_formatted: this.formatDate(item.date),
      total_sequences: parseInt(item.total_sequences) || 0,
      risk_distribution: this.processRiskDistribution(item.risk_distribution),
      average_risk: parseFloat(item.average_risk_score) || 0,
      critical_count: parseInt(item.risk_distribution?.CRITICAL) || 0,
      novel_mutations: parseInt(item.novel_mutations_count) || 0
    }));
  }

  formatDailyStats(stats) {
    if (!Array.isArray(stats)) return [];
    
    return stats.map(stat => ({
      ...stat,
      date_formatted: this.formatDate(stat.date),
      processing_time_formatted: this.formatProcessingTime(stat.processing_time_today)
    }));
  }

  formatProcessingTime(timeInSeconds) {
  if (!timeInSeconds) return 'Unknown';
  
  const time = parseFloat(timeInSeconds);
  if (time < 60) return `${time.toFixed(1)}s`;
  if (time < 3600) return `${(time / 60).toFixed(1)}m`;
  return `${(time / 3600).toFixed(1)}h`;
}

formatTemporalChart(data) {
  if (!Array.isArray(data)) return [];
  
  return data.map(item => ({
    date: item.date,
    date_formatted: this.formatDate(item.date),
    total_sequences: parseInt(item.total_sequences) || 0,
    critical_sequences: parseInt(item.risk_distribution?.CRITICAL) || 0,
    high_sequences: parseInt(item.risk_distribution?.HIGH) || 0,
    medium_sequences: parseInt(item.risk_distribution?.MEDIUM) || 0,
    low_sequences: parseInt(item.risk_distribution?.LOW) || 0,
    novel_mutations: parseInt(item.novel_mutations_count) || 0,
    average_risk_score: parseFloat(item.average_risk_score) || 0,
    // For line charts showing trends over time
    timestamp: new Date(item.date).getTime()
  })).sort((a, b) => a.timestamp - b.timestamp);
}

calculateTrends(timeline) {
  if (!Array.isArray(timeline) || timeline.length < 2) return {};
  
  const latest = timeline[timeline.length - 1];
  const previous = timeline[timeline.length - 2];
  
  return {
    sequence_trend: latest.total_sequences - previous.total_sequences,
    risk_trend: latest.average_risk - previous.average_risk,
    critical_trend: latest.critical_count - previous.critical_count
  };
}
}

// Create singleton instance
const dataProcessor = new DataProcessor();

export default dataProcessor;
    